import { useEffect, useState, useCallback } from 'react';
import type { OptInNotificationContext } from '@scale/genai-compass-shared/cards/opt_in_notification';
import { useCompassCard } from '@scale/compass-ui/store';
import { ColumnSubType } from '@scale/compass-shared/types';
import { AssignmentEntry } from '@scale/shared/defaults';

const useOptInNotificationCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit, error } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext as OptInNotificationContext;
  const data = retrievedOutput?.response?.data ?? [];
  const columns = retrievedOutput?.response?.schema ?? [];

  const [numWorkers, setNumWorkers] = useState(0);
  const [initialColumnSet, setInitialColumnSet] = useState(false);

  const setAssignment = useCallback(
    (assignment: AssignmentEntry) => {
      updateCardContext({
        ...context,
        assignment,
      } as OptInNotificationContext);

      if (numWorkers > 0 && assignment.projectId) {
        updateCardCanSubmit(true);
      } else {
        updateCardCanSubmit(false);
      }
    },
    [context, numWorkers, updateCardContext, updateCardCanSubmit],
  );

  const setColumnIdWithWorkerIds = useCallback(
    (columnIdWithWorkerIds: string) => {
      const columnId = columnIdWithWorkerIds;
      updateCardContext({
        ...context,
        columnIdWithWorkerIds: columnId,
      } as OptInNotificationContext);

      if (!columnId) {
        setNumWorkers(0);
        updateCardCanSubmit(false);
        return;
      }

      const workerRows = data.filter((row: Record<string, any>) => row[columnId]);
      setNumWorkers(workerRows.length);

      if (workerRows.length > 0 && context.assignment?.projectId) {
        updateCardCanSubmit(true);
      }
    },
    [context, data, updateCardContext, updateCardCanSubmit],
  );

  useEffect(() => {
    if (!initialColumnSet && columns.length > 0) {
      for (const column of columns) {
        if (column.subType === ColumnSubType.WORKER_ID) {
          setColumnIdWithWorkerIds(column.id);
          setInitialColumnSet(true);
          break;
        }
      }
    }
  }, []);

  return {
    assignment: context.assignment,
    setAssignment,
    columnIdWithWorkerIds: context.columnIdWithWorkerIds,
    setColumnIdWithWorkerIds,
    numWorkers,
    data,
    card,
    hasError: !!error,
    columns,
  };
};
export default useOptInNotificationCard;
